import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, IndianRupee, Pencil, Plus, Star, Trash2, Truck } from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import {
  DataTable,
  KpiCard,
  Panel,
  inr,
  num,
  pct0,
  type DataColumn,
} from "@/components/admin/analytics/AnalyticsKit";
import {
  FieldGroup,
  NotesField,
  NumberField,
  SelectField,
  TextField,
} from "@/components/admin/delivery/form-bits";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  deleteInventorySupplier,
  listInventorySuppliers,
  saveInventorySupplier,
} from "@/lib/admin/inventory.functions";
import {
  PAYMENT_TERMS,
  PAYMENT_TERM_LABEL,
  SUPPLIER_STATUSES,
  SUPPLIER_STATUS_LABEL,
  supplierSchema,
  type InventorySupplier,
  type SupplierInput,
} from "@/lib/admin/inventory.schemas";

export const Route = createFileRoute("/_admin/admin/inventory-suppliers")({
  head: () => ({
    meta: [
      { title: "Suppliers — Admin" },
      {
        name: "description",
        content:
          "Mumbai ingredient and packaging suppliers with GST, contacts, payment terms and bank details.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SuppliersPage,
});

function emptyForm(): SupplierInput {
  return {
    supplier_code: "",
    supplier_name: "",
    gst_number: "",
    pan_number: "",
    contact_person: "",
    phone: "",
    whatsapp: "",
    email: "",
    address_line: "",
    city: "Mumbai",
    state: "Maharashtra",
    pincode: "",
    payment_terms: "net_15",
    bank_name: "",
    bank_account_name: "",
    bank_account_number: "",
    bank_ifsc: "",
    rating: 4,
    on_time_rate: 0.9,
    status: "active",
    notes: "",
  };
}

function SuppliersPage() {
  const queryClient = useQueryClient();
  const listFn = useServerFn(listInventorySuppliers);
  const saveFn = useServerFn(saveInventorySupplier);
  const deleteFn = useServerFn(deleteInventorySupplier);

  const suppliers = useQuery({
    queryKey: ["admin", "inventory", "suppliers"],
    queryFn: () => listFn(),
    staleTime: 15_000,
  });

  const [editing, setEditing] = useState<InventorySupplier | "new" | null>(null);
  const [form, setForm] = useState<SupplierInput>(emptyForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmDelete, setConfirmDelete] = useState<InventorySupplier | null>(null);

  useEffect(() => {
    if (editing === "new") setForm(emptyForm());
    else if (editing) {
      const { id, item_count, po_count, purchased_paise, ...rest } = editing;
      void id;
      void item_count;
      void po_count;
      void purchased_paise;
      setForm({
        ...emptyForm(),
        ...rest,
        payment_terms: rest.payment_terms as SupplierInput["payment_terms"],
        status: rest.status as SupplierInput["status"],
      });
    }
    setErrors({});
  }, [editing]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["admin", "inventory"] });

  const save = useMutation({
    mutationFn: (v: { id: string | null; patch: SupplierInput }) => saveFn({ data: v as never }),
    onSuccess: async () => {
      toast.success("Supplier saved");
      setEditing(null);
      await invalidate();
    },
    onError: () => toast.error("Couldn't save supplier."),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } as never }),
    onSuccess: async () => {
      toast.success("Supplier removed");
      setConfirmDelete(null);
      await invalidate();
    },
    onError: () => toast.error("Couldn't remove supplier."),
  });

  const rows = useMemo(() => suppliers.data ?? [], [suppliers.data]);
  const kpi = useMemo(
    () => ({
      total: rows.length,
      active: rows.filter((r) => r.status === "active").length,
      spend: rows.reduce((s, r) => s + r.purchased_paise, 0),
      otd: rows.length ? rows.reduce((s, r) => s + r.on_time_rate, 0) / rows.length : 0,
    }),
    [rows],
  );

  const submit = () => {
    const parsed = supplierSchema.safeParse(form);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) next[String(issue.path[0])] = issue.message;
      setErrors(next);
      return;
    }
    save.mutate({ id: editing === "new" || !editing ? null : editing.id, patch: parsed.data });
  };

  const columns: Array<DataColumn<InventorySupplier>> = [
    {
      key: "name",
      header: "Supplier",
      render: (r) => (
        <div>
          <p className="font-medium text-foreground">{r.supplier_name}</p>
          <p className="text-[11px] text-muted-foreground">
            {r.supplier_code || "—"} · {r.city}
          </p>
        </div>
      ),
      sortValue: (r) => r.supplier_name,
      exportValue: (r) => r.supplier_name,
    },
    {
      key: "contact",
      header: "Contact",
      render: (r) => (
        <div className="text-xs">
          <p className="text-foreground">{r.contact_person || "—"}</p>
          <p className="text-muted-foreground">{r.phone || "—"}</p>
        </div>
      ),
      exportValue: (r) => `${r.contact_person} ${r.phone}`,
    },
    {
      key: "gst",
      header: "GST",
      render: (r) => <span className="font-mono text-[11px]">{r.gst_number || "—"}</span>,
      exportValue: (r) => r.gst_number,
    },
    {
      key: "terms",
      header: "Payment terms",
      render: (r) => PAYMENT_TERM_LABEL[r.payment_terms] ?? r.payment_terms,
      exportValue: (r) => PAYMENT_TERM_LABEL[r.payment_terms] ?? r.payment_terms,
    },
    {
      key: "items",
      header: "Items",
      align: "right",
      render: (r) => num(r.item_count),
      sortValue: (r) => r.item_count,
      exportValue: (r) => r.item_count,
    },
    {
      key: "pos",
      header: "POs",
      align: "right",
      render: (r) => num(r.po_count),
      sortValue: (r) => r.po_count,
      exportValue: (r) => r.po_count,
    },
    {
      key: "spend",
      header: "Purchased",
      align: "right",
      render: (r) => inr(r.purchased_paise / 100),
      sortValue: (r) => r.purchased_paise,
      exportValue: (r) => r.purchased_paise / 100,
    },
    {
      key: "otd",
      header: "On time",
      align: "right",
      render: (r) => pct0(r.on_time_rate),
      sortValue: (r) => r.on_time_rate,
      exportValue: (r) => r.on_time_rate,
    },
    {
      key: "status",
      header: "Status",
      render: (r) => (
        <Badge variant={r.status === "active" ? "secondary" : "outline"}>
          {SUPPLIER_STATUS_LABEL[r.status] ?? r.status}
        </Badge>
      ),
      exportValue: (r) => SUPPLIER_STATUS_LABEL[r.status] ?? r.status,
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (r) => (
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="outline" className="h-8" onClick={() => setEditing(r)}>
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-destructive"
            onClick={() => setConfirmDelete(r)}
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title="Suppliers"
        description="Every vendor the kitchen buys from — contacts, GST, payment terms and bank details."
      />

      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard
          label="Suppliers"
          value={num(kpi.total)}
          tone="info"
          icon={Building2}
          loading={suppliers.isPending}
        />
        <KpiCard
          label="Active"
          value={num(kpi.active)}
          tone="success"
          icon={Truck}
          loading={suppliers.isPending}
        />
        <KpiCard
          label="Total purchased"
          value={inr(kpi.spend / 100)}
          tone="teal"
          icon={IndianRupee}
          loading={suppliers.isPending}
        />
        <KpiCard
          label="Average on-time"
          value={pct0(kpi.otd)}
          tone="purple"
          icon={Star}
          loading={suppliers.isPending}
        />
      </div>

      <Panel title="Supplier master" subtitle={`${rows.length} suppliers`}>
        <div className="mb-3 flex justify-end">
          <Button size="sm" className="gap-1.5" onClick={() => setEditing("new")}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            New supplier
          </Button>
        </div>
        <DataTable
          rows={rows}
          columns={columns}
          loading={suppliers.isPending}
          exportName="inventory-suppliers"
          importModule="suppliers"
          pageSize={12}
          minWidth={900}
          searchOn={(r) => `${r.supplier_name} ${r.contact_person} ${r.phone} ${r.gst_number}`}
        />
      </Panel>

      <Dialog open={editing !== null} onOpenChange={(o) => (o ? null : setEditing(null))}>
        <DialogContent className="max-h-[88vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "New supplier" : "Edit supplier"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <FieldGroup title="Business details">
              <TextField
                label="Supplier name"
                value={form.supplier_name}
                onChange={(v) => setForm({ ...form, supplier_name: v })}
                error={errors["supplier_name"]}
              />
              <TextField
                label="Supplier code"
                value={form.supplier_code}
                onChange={(v) => setForm({ ...form, supplier_code: v })}
              />
              <TextField
                label="GST number"
                value={form.gst_number}
                onChange={(v) => setForm({ ...form, gst_number: v })}
              />
              <TextField
                label="PAN"
                value={form.pan_number}
                onChange={(v) => setForm({ ...form, pan_number: v })}
              />
              <SelectField
                label="Payment terms"
                value={form.payment_terms}
                onChange={(v) => setForm({ ...form, payment_terms: v as SupplierInput["payment_terms"] })}
                options={PAYMENT_TERMS.map((t) => ({ value: t, label: PAYMENT_TERM_LABEL[t] ?? t }))}
              />
              <SelectField
                label="Status"
                value={form.status}
                onChange={(v) => setForm({ ...form, status: v as SupplierInput["status"] })}
                options={SUPPLIER_STATUSES.map((t) => ({
                  value: t,
                  label: SUPPLIER_STATUS_LABEL[t] ?? t,
                }))}
              />
            </FieldGroup>

            <FieldGroup title="Contact">
              <TextField
                label="Contact person"
                value={form.contact_person}
                onChange={(v) => setForm({ ...form, contact_person: v })}
              />
              <TextField
                label="Phone"
                value={form.phone}
                onChange={(v) => setForm({ ...form, phone: v })}
              />
              <TextField
                label="WhatsApp"
                value={form.whatsapp}
                onChange={(v) => setForm({ ...form, whatsapp: v })}
              />
              <TextField
                label="Email"
                value={form.email}
                onChange={(v) => setForm({ ...form, email: v })}
              />
              <TextField
                label="Address"
                value={form.address_line}
                onChange={(v) => setForm({ ...form, address_line: v })}
                className="sm:col-span-2"
              />
              <TextField
                label="City"
                value={form.city}
                onChange={(v) => setForm({ ...form, city: v })}
              />
              <TextField
                label="State"
                value={form.state}
                onChange={(v) => setForm({ ...form, state: v })}
              />
              <TextField
                label="Pincode"
                value={form.pincode}
                onChange={(v) => setForm({ ...form, pincode: v })}
              />
            </FieldGroup>

            <FieldGroup title="Bank & performance">
              <TextField
                label="Bank name"
                value={form.bank_name}
                onChange={(v) => setForm({ ...form, bank_name: v })}
              />
              <TextField
                label="Account name"
                value={form.bank_account_name}
                onChange={(v) => setForm({ ...form, bank_account_name: v })}
              />
              <TextField
                label="Account number"
                value={form.bank_account_number}
                onChange={(v) => setForm({ ...form, bank_account_number: v })}
              />
              <TextField
                label="IFSC"
                value={form.bank_ifsc}
                onChange={(v) => setForm({ ...form, bank_ifsc: v })}
              />
              <NumberField
                label="Rating"
                step={0.1}
                value={form.rating}
                onChange={(v) => setForm({ ...form, rating: v })}
                suffix="0–5"
              />
              <NumberField
                label="On-time rate"
                step={0.01}
                value={form.on_time_rate}
                onChange={(v) => setForm({ ...form, on_time_rate: v })}
                suffix="0–1"
              />
              <NotesField value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} />
            </FieldGroup>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save supplier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmDelete !== null}
        onOpenChange={(o) => (o ? null : setConfirmDelete(null))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirmDelete?.supplier_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Purchase history stays intact; linked items lose their preferred supplier.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmDelete && remove.mutate(confirmDelete.id)}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
